import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, App, Button, Card, Descriptions, Dropdown, Empty, Input, Modal, Select, Space, Table, Tag, Timeline, Tooltip, Typography } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import dayjs from 'dayjs';
import recognitionService, {
  ADJUSTMENT_ALLOWED_STATUSES,
  LINE_STATUSES,
  REVISION_TYPES,
  type Adjustment,
  type ContractDetail,
  type ContractEvent,
  type RevisionType,
  type ScheduleLineRow,
} from '@/services/financeRecognition.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_RECOGNITION_REVISION } from '@/constants/approval-entity-types';
import { StatusTag, amount, apiErrorMessage, asObject, contractPath, humanize, jvPath, options, useRecognitionAccess } from './prepaidDisplay';

const { Text } = Typography;

const POSTABLE = ['PLANNED', 'DUE', 'POSTING_BLOCKED'];
const POSTED = ['POSTED', 'PARTIALLY_POSTED'];

export const adjustPath = (contractId: string, type: RevisionType, extra?: Record<string, string>) => {
  const q = new URLSearchParams({ type, ...(extra ?? {}) });
  return `${contractPath(contractId)}/adjust?${q.toString()}`;
};

/** Adjustment types the current user may start on this contract. */
export function useAvailableAdjustments(contract: ContractDetail | undefined) {
  const access = useRecognitionAccess();
  return useMemo(
    () =>
      contract
        ? REVISION_TYPES.filter((t) => ADJUSTMENT_ALLOWED_STATUSES[t].includes(contract.status) && access.canAdjust(t)).filter(
            (t) => t !== 'REVERSAL'
          )
        : [],
    [contract, access]
  );
}

export const AdjustMenu: React.FC<{ contract: ContractDetail }> = ({ contract }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const types = useAvailableAdjustments(contract);
  if (!types.length) return null;
  return (
    <Dropdown
      menu={{
        items: types.map((t) => ({ key: t, label: humanize(t) })),
        onClick: ({ key }) => navigate(adjustPath(contract.id, key as RevisionType)),
      }}
    >
      <Button>
        {t('adjust')} <DownOutlined />
      </Button>
    </Dropdown>
  );
};

// ---------------------------------------------------------------- Schedule
export const ScheduleTab: React.FC<{ contract: ContractDetail }> = ({ contract }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const access = useRecognitionAccess();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [status, setStatus] = useState<string[]>([]);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recognition', 'contract', contract.id, 'schedule', page, pageSize, status, includeCancelled],
    queryFn: () =>
      recognitionService.contractSchedule(contract.id, {
        page,
        limit: pageSize,
        status: status.length ? status.join(',') : undefined,
        include_cancelled: includeCancelled,
      }),
    placeholderData: keepPreviousData,
  });

  const postMutation = useMutation({
    mutationFn: (ids: string[]) => recognitionService.manualPost(ids),
    onSuccess: (outcomes) => {
      const failed = outcomes.filter((o) => o.status === 'EXCEPTION');
      const posted = outcomes.filter((o) => o.status === 'POSTED').length;
      const drafts = outcomes.filter((o) => o.status === 'DRAFT_CREATED').length;
      if (failed.length) message.warning(`${posted} posted, ${drafts} drafted, ${failed.length} failed: ${failed[0].message ?? failed[0].exceptionCode}`);
      else message.success(drafts ? `${drafts} draft journal(s) created for approval` : `${posted} line(s) recognised`);
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: ['recognition'] });
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Posting failed')),
  });

  const canRecognize = access.canPost && ['SCHEDULED', 'ACTIVE'].includes(contract.status);
  const canReverse = access.canAdjust('REVERSAL') && ADJUSTMENT_ALLOWED_STATUSES.REVERSAL.includes(contract.status);
  const ccy = contract.transaction_currency;

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space wrap>
        <Select
          mode="multiple"
          allowClear
          placeholder={t('filterStatus')}
          style={{ minWidth: 260 }}
          value={status}
          options={options(LINE_STATUSES)}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
        />
        <Select
          style={{ width: 200 }}
          value={includeCancelled ? 'all' : 'active'}
          options={[
            { value: 'active', label: t('hideCancelledLines') },
            { value: 'all', label: t('includeCancelledHistory') },
          ]}
          onChange={(v) => setIncludeCancelled(v === 'all')}
        />
        {canRecognize && (
          <Button type="primary" disabled={!selected.length} loading={postMutation.isPending} onClick={() => postMutation.mutate(selected)}>
            {t('recogniseSelectedCount', { count: selected.length })}
          </Button>
        )}
        {data?.totals && (
          <Text type="secondary">
            {t('scheduleTotalsLine', {
              scheduled: amount(data.totals.scheduled, ccy),
              posted: amount(data.totals.posted, ccy),
              open: amount(data.totals.open_amount, ccy),
            })}
          </Text>
        )}
      </Space>
      {isError && <Alert type="error" showIcon message={apiErrorMessage(error)} />}
      <Table<ScheduleLineRow>
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data?.data ?? []}
        scroll={{ x: 1300 }}
        rowSelection={
          canRecognize
            ? {
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys as string[]),
                getCheckboxProps: (r) => ({ disabled: !POSTABLE.includes(r.status) }),
              }
            : undefined
        }
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100, 200],
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        columns={[
          { title: '#', dataIndex: 'sequence_no', width: 50 },
          { title: t('period'), dataIndex: 'accounting_period_key', width: 90 },
          { title: t('from'), dataIndex: 'recognition_from_date', width: 110 },
          { title: t('to'), dataIndex: 'recognition_to_date', width: 110 },
          { title: t('days'), dataIndex: 'recognition_days', width: 60, align: 'right' },
          { title: t('planned'), dataIndex: 'planned_amount', width: 120, align: 'right', render: (v: string) => amount(v) },
          { title: tc('posted'), dataIndex: 'posted_amount', width: 120, align: 'right', render: (v: string) => amount(v) },
          { title: t('remaining'), dataIndex: 'remaining_amount', width: 120, align: 'right', render: (v: string) => amount(v) },
          {
            title: t('postingDate'),
            key: 'pd',
            width: 120,
            render: (_: unknown, r) =>
              r.posting_date_override ? (
                <Tooltip title={`Originally ${r.posting_date}`}>
                  <Text underline>{r.posting_date_override}</Text>
                </Tooltip>
              ) : (
                r.posting_date
              ),
          },
          {
            title: tc('status'),
            dataIndex: 'status',
            width: 150,
            render: (s: string, r) => (
              <Space direction="vertical" size={0}>
                <StatusTag status={s} />
                {r.exception_message && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {r.exception_message}
                  </Text>
                )}
                {Boolean(r.is_manual) && <Tag>{t('custom')}</Tag>}
              </Space>
            ),
          },
          {
            title: t('jv'),
            key: 'jv',
            width: 170,
            render: (_: unknown, r) => (
              <Space direction="vertical" size={0}>
                {r.journal_voucher_id && <Link to={jvPath(r.journal_voucher_id)}>{r.journal_voucher_number}</Link>}
                {!r.journal_voucher_id && r.draft_journal_voucher_id && (
                  <Link to={jvPath(r.draft_journal_voucher_id)}>{r.draft_journal_voucher_number} (draft)</Link>
                )}
                {r.reversal_journal_voucher_id && (
                  <Link to={jvPath(r.reversal_journal_voucher_id)}>
                    <Text type="secondary">↺ {r.reversal_journal_voucher_number}</Text>
                  </Link>
                )}
              </Space>
            ),
          },
          {
            title: t('run'),
            dataIndex: 'recognition_run_id',
            width: 70,
            render: (v: string | null) => (v ? <Link to={`/fin/prepaid/runs/${v}`}>{t('open')}</Link> : null),
          },
          {
            title: '',
            key: 'act',
            width: 90,
            render: (_: unknown, r) =>
              canReverse && POSTED.includes(r.status) ? (
                <Button size="small" onClick={() => navigate(adjustPath(contract.id, 'REVERSAL', { line: r.id }))}>
                  {t('reverse')}
                </Button>
              ) : null,
          },
        ]}
      />
      <Text type="secondary">{t('postedLinesImmutableHint')}</Text>
    </Space>
  );
};

// ---------------------------------------------------------------- Adjustments
export const AdjustmentsTab: React.FC<{ contract: ContractDetail }> = ({ contract }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const access = useRecognitionAccess();
  const types = useAvailableAdjustments(contract);
  const [rejectTarget, setRejectTarget] = useState<Adjustment | null>(null);
  const [reason, setReason] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ['recognition', 'contract', contract.id, 'adjustments'],
    queryFn: () => recognitionService.contractAdjustments(contract.id),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['recognition'] });
  const approve = useMutation({
    mutationFn: (id: string) => recognitionService.approveAdjustment(id),
    onSuccess: (r) => {
      message.success(r.status === 'APPLIED' ? 'Adjustment approved and applied' : 'Adjustment approved');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Approval failed')),
  });
  const reject = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => recognitionService.rejectAdjustment(id, { reason: text }),
    onSuccess: () => {
      message.success(t('adjustmentRejected'));
      setRejectTarget(null);
      setReason('');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Rejection failed')),
  });
  const retry = useMutation({
    mutationFn: (id: string) => recognitionService.retryAdjustment(id),
    onSuccess: () => {
      message.success(t('adjustmentReApplied'));
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Retry failed')),
  });

  const expandedRow = data.find((a) => a.id === expanded);

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {types.length > 0 && (
        <Space wrap>
          {types.map((t) => (
            <Button key={t} onClick={() => navigate(adjustPath(contract.id, t))}>
              {humanize(t)}
            </Button>
          ))}
        </Space>
      )}
      {!types.length && <Text type="secondary">No adjustments are available for a contract in status {humanize(contract.status).toLowerCase()} with your permissions.</Text>}
      <Table<Adjustment>
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1300 }}
        onRow={(r) => ({ onClick: () => setExpanded(r.id === expanded ? null : r.id), style: { cursor: 'pointer' } })}
        columns={[
          { title: '#', dataIndex: 'adjustment_no', width: 50 },
          { title: t('date'), dataIndex: 'effective_date', width: 110 },
          { title: t('type'), dataIndex: 'revision_type', width: 140, render: (t: string) => <Tag>{humanize(t)}</Tag> },
          { title: t('amount'), dataIndex: 'amount', width: 130, align: 'right', render: (v: string) => (Number(v) ? amount(v, contract.transaction_currency) : '—') },
          { title: t('reason'), dataIndex: 'reason', ellipsis: true },
          {
            title: t('approvalStatus'),
            dataIndex: 'status',
            width: 160,
            render: (s: string, r) => (
              <Space direction="vertical" size={0}>
                <StatusTag status={s} />
                {r.failure_message && <Text type="danger" style={{ fontSize: 12 }}>{r.failure_message}</Text>}
                {r.rejection_reason && <Text type="secondary" style={{ fontSize: 12 }}>{r.rejection_reason}</Text>}
              </Space>
            ),
          },
          {
            title: t('user'),
            key: 'user',
            width: 170,
            render: (_: unknown, r) => (
              <Space direction="vertical" size={0}>
                <Text>{r.created_by_name || '—'}</Text>
                {r.approved_by_name && <Text type="secondary" style={{ fontSize: 12 }}>Approved: {r.approved_by_name}</Text>}
              </Space>
            ),
          },
          {
            title: t('linkedJv'),
            key: 'jv',
            width: 150,
            render: (_: unknown, r) =>
              r.journal_voucher_id ? (
                <Link to={jvPath(r.journal_voucher_id)} onClick={(e) => e.stopPropagation()}>
                  {r.journal_voucher_number}
                </Link>
              ) : r.child_contract_id ? (
                <Link to={contractPath(r.child_contract_id)} onClick={(e) => e.stopPropagation()}>
                  {t('childContract')}
                </Link>
              ) : (
                '—'
              ),
          },
          {
            title: tc('actions'),
            key: 'act',
            width: 190,
            render: (_: unknown, r) => (
              <Space onClick={(e) => e.stopPropagation()}>
                {r.status === 'PENDING_APPROVAL' && !r.approval_instance_id && access.canApprove && (
                  <>
                    <Button size="small" type="primary" loading={approve.isPending && approve.variables === r.id} onClick={() => approve.mutate(r.id)}>
                      {tc('approve')}
                    </Button>
                    <Button size="small" danger onClick={() => setRejectTarget(r)}>
                      {tc('reject')}
                    </Button>
                  </>
                )}
                {r.status === 'FAILED' && access.canAdjust(r.revision_type) && (
                  <Button size="small" loading={retry.isPending && retry.variables === r.id} onClick={() => retry.mutate(r.id)}>
                    {t('retry')}
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />
      {expandedRow && (
        <Card size="small" title={`Adjustment #${expandedRow.adjustment_no} — ${humanize(expandedRow.revision_type)}`}>
          <Descriptions size="small" column={3}>
            <Descriptions.Item label={t('remainingBefore')}>{amount(expandedRow.remaining_amount_before)}</Descriptions.Item>
            <Descriptions.Item label={t('remainingAfter')}>{amount(expandedRow.remaining_amount_after)}</Descriptions.Item>
            <Descriptions.Item label={t('revision')}>{expandedRow.revision_no ?? '—'}</Descriptions.Item>
            {expandedRow.applied_at && <Descriptions.Item label={t('applied')}>{dayjs(expandedRow.applied_at).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>}
          </Descriptions>
          <SnapshotDiff before={expandedRow.previous_snapshot} after={expandedRow.new_snapshot} />
          {expandedRow.status === 'PENDING_APPROVAL' && expandedRow.approval_instance_id && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_RECOGNITION_REVISION}
              entityId={expandedRow.id}
              onApproveViaDocument={async (remarks) => {
                await recognitionService.approveAdjustment(expandedRow.id, remarks);
              }}
              onRejectViaDocument={async (payload) => {
                await recognitionService.rejectAdjustment(expandedRow.id, payload);
              }}
              extraInvalidateQueryKeys={[['recognition']]}
            />
          )}
        </Card>
      )}
      <Modal
        open={!!rejectTarget}
        title={`Reject adjustment #${rejectTarget?.adjustment_no ?? ''}`}
        okText={tc('reject')}
        okButtonProps={{ danger: true, disabled: reason.trim().length < 3 }}
        confirmLoading={reject.isPending}
        onCancel={() => {
          setRejectTarget(null);
          setReason('');
        }}
        onOk={() => rejectTarget && reject.mutate({ id: rejectTarget.id, text: reason.trim() })}
      >
        <Input.TextArea rows={3} placeholder={t('reasonRequired')} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </Space>
  );
};

const SNAPSHOT_FIELDS: Array<[string, string]> = [
  ['status', 'Status'],
  ['recognizable_amount', 'Recognisable'],
  ['remaining_amount', 'Remaining'],
  ['service_end_date', 'Service end'],
  ['recognition_end_date', 'Recognition end'],
  ['recognition_method', 'Method'],
  ['day_count_method', 'Day count'],
  ['expense_account_id', 'Expense account'],
  ['cost_center', 'Cost centre'],
  ['project_id', 'Project'],
  ['department_id', 'Department'],
  ['current_revision', 'Revision'],
];

const SnapshotDiff: React.FC<{ before: unknown; after: unknown }> = ({ before: rawBefore, after: rawAfter }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const before = asObject(rawBefore);
  const after = asObject(rawAfter);
  if (!Object.keys(before).length || !Object.keys(after).length) return null;
  const rows = SNAPSHOT_FIELDS.filter(([k]) => String(before[k] ?? '') !== String(after[k] ?? '')).map(([k, label]) => ({
    key: k,
    label,
    before: before[k] == null ? '—' : String(before[k]),
    after: after[k] == null ? '—' : String(after[k]),
  }));
  if (!rows.length) return null;
  return (
    <Table
      size="small"
      style={{ marginTop: 8 }}
      rowKey="key"
      pagination={false}
      dataSource={rows}
      columns={[
        { title: t('field'), dataIndex: 'label', width: 180 },
        { title: t('before'), dataIndex: 'before' },
        { title: t('after'), dataIndex: 'after' },
      ]}
    />
  );
};

// ---------------------------------------------------------------- Journal entries
export const JournalsTab: React.FC<{ contract: ContractDetail }> = ({ contract }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { data: journals = [], isLoading } = useQuery({
    queryKey: ['recognition', 'contract', contract.id, 'journals'],
    queryFn: () => recognitionService.contractJournals(contract.id),
  });
  const { data: movements = [], isLoading: movLoading } = useQuery({
    queryKey: ['recognition', 'contract', contract.id, 'movements'],
    queryFn: () => recognitionService.contractMovements(contract.id),
  });
  const { data: lines } = useQuery({
    queryKey: ['recognition', 'contract', contract.id, 'schedule', 'journal-map'],
    queryFn: () => recognitionService.contractSchedule(contract.id, { limit: 500, include_cancelled: true }),
  });
  const lineByJv = useMemo(() => {
    const map = new Map<string, { periods: string[]; seqs: number[] }>();
    for (const l of lines?.data ?? []) {
      for (const jv of [l.journal_voucher_id, l.draft_journal_voucher_id, l.reversal_journal_voucher_id]) {
        if (!jv) continue;
        const e = map.get(jv) ?? { periods: [], seqs: [] };
        if (!e.periods.includes(l.accounting_period_key)) e.periods.push(l.accounting_period_key);
        e.seqs.push(l.sequence_no);
        map.set(jv, e);
      }
    }
    return map;
  }, [lines]);

  const base = contract.base_currency;
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Table
        size="small"
        rowKey={(r) => `${r.id}-${r.role}`}
        loading={isLoading}
        dataSource={journals}
        pagination={{ pageSize: 25 }}
        locale={{ emptyText: <Empty description="No journals yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        columns={[
          { title: t('jvNumber'), dataIndex: 'voucher_number', render: (v: string, r) => <Link to={jvPath(r.id)}>{v}</Link> },
          { title: t('postingDate'), dataIndex: 'voucher_date', width: 120 },
          { title: t('type'), dataIndex: 'role', width: 150, render: (v: string) => <Tag>{humanize(v)}</Tag> },
          { title: `Debit (${base})`, dataIndex: 'total_debit', width: 140, align: 'right', render: (v: string) => amount(v) },
          { title: `Credit (${base})`, dataIndex: 'total_debit', key: 'credit', width: 140, align: 'right', render: (v: string) => amount(v) },
          { title: tc('status'), dataIndex: 'status', width: 110, render: (s: string) => <Tag color={s === 'Posted' ? 'green' : s === 'Cancelled' ? 'red' : 'default'}>{s}</Tag> },
          { title: t('recognitionPeriod'), key: 'period', width: 150, render: (_: unknown, r) => lineByJv.get(r.id)?.periods.join(', ') || '—' },
          { title: t('scheduleLine'), key: 'line', width: 120, render: (_: unknown, r) => (lineByJv.get(r.id)?.seqs.map((s) => `#${s}`).join(', ') || '—') },
          { title: t('description'), dataIndex: 'description', ellipsis: true },
        ]}
      />
      <Card size="small" title={t('prepaidSubledgerMovements')}>
        <Table
          size="small"
          rowKey="id"
          loading={movLoading}
          dataSource={movements}
          pagination={{ pageSize: 25 }}
          columns={[
            { title: t('date'), dataIndex: 'movement_date', width: 110 },
            { title: t('movement'), dataIndex: 'movement_type', width: 180, render: (v: string) => humanize(v) },
            { title: `Amount (${contract.transaction_currency})`, dataIndex: 'amount', width: 150, align: 'right', render: (v: string) => amount(v) },
            { title: `Base (${base})`, dataIndex: 'base_amount', width: 150, align: 'right', render: (v: string) => amount(v) },
            {
              title: t('inGl'),
              dataIndex: 'in_gl',
              width: 110,
              render: (v: number | boolean) => (Number(v) ? <Tag color="green">{t('engineJv')}</Tag> : <Tag>{t('preExisting')}</Tag>),
            },
            { title: t('jv'), key: 'jv', width: 140, render: (_: unknown, r) => (r.journal_voucher_id ? <Link to={jvPath(r.journal_voucher_id)}>{r.voucher_number}</Link> : '—') },
            { title: t('description'), dataIndex: 'description', ellipsis: true },
          ]}
        />
      </Card>
    </Space>
  );
};

// ---------------------------------------------------------------- Activity
const EVENT_COLOR: Record<string, string> = {
  CONTRACT_CREATED: 'blue',
  CONTRACT_APPROVED: 'green',
  CONTRACT_ACTIVATED: 'green',
  JV_POSTED: 'green',
  CONTRACT_REJECTED: 'red',
  CONTRACT_CANCELLED: 'red',
  POSTING_BLOCKED: 'red',
  REVISION_REJECTED: 'red',
  CONTRACT_SUSPENDED: 'orange',
  CONTRACT_TERMINATED: 'red',
  SOURCE_DOCUMENT_REVERSED: 'red',
};

function eventTitle(e: ContractEvent): string {
  const meta = asObject(e.metadata);
  if (typeof meta.description === 'string' && meta.description) return meta.description;
  const after = asObject(e.after_json);
  const periods = Array.isArray(after.periods) ? (after.periods as string[]).join(', ') : '';
  const jv = after.journal_voucher_number || after.draft_journal_voucher_number;
  return [humanize(e.event_type), periods ? `(${periods})` : '', jv ? `— ${jv}` : ''].filter(Boolean).join(' ');
}

export const ActivityTab: React.FC<{ contract: ContractDetail }> = ({ contract }) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { data = [], isLoading } = useQuery({
    queryKey: ['recognition', 'contract', contract.id, 'events'],
    queryFn: () => recognitionService.contractEvents(contract.id),
  });
  if (isLoading) return <Card loading />;
  if (!data.length) return <Empty description="No activity yet" />;
  return (
    <Timeline
      items={data.map((e) => ({
        key: e.id,
        color: EVENT_COLOR[e.event_type] ?? 'gray',
        children: (
          <Space direction="vertical" size={0}>
            <Text strong>{eventTitle(e)}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(e.event_at).format('YYYY-MM-DD HH:mm')} · {e.actor_name?.trim() || humanize(e.event_source)}
            </Text>
            {e.reason && <Text style={{ fontSize: 12 }}>{e.reason}</Text>}
            {e.journal_voucher_id && <Link to={jvPath(e.journal_voucher_id)}>{t('openJournal')}</Link>}
            {e.recognition_run_id && <Link to={`/fin/prepaid/runs/${e.recognition_run_id}`}>{t('openRecognitionRun')}</Link>}
          </Space>
        ),
      }))}
    />
  );
};
